import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import CommunicationNotifier from '../../components/ui/CommunicationNotifier';
import Icon from '../../components/AppIcon';
import PostCard from './components/PostCard';
import CreatePostModal from './components/CreatePostModal';
import TrendingSidebar from './components/TrendingSidebar';
import FilterBar from './components/FilterBar';
import CreatePostFab from './components/CreatePostFab';
import ReactionsModal from './components/ReactionsModal';
import AwardsSettingsModal from './components/AwardsSettingsModal';
import { api } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { fetchClassesInMySchool, type ClassroomInSchool } from '../../lib/classroomsApi';
import SubscriptionRequiredModal from '../../components/ui/SubscriptionRequiredModal';
import { isSubscriptionError, type SubscriptionFeature } from '../../lib/subscriptionGate';

import type { CommunityHighlight, CreatePostData, Post, PostFilter, SchoolEvent, TrendingTopic, User } from './types';

type ApiPost = {
  id: number;
  author: { id: number; name: string | null; role: string | null; avatar_url?: string | null };
  content: string;
  category: string;
  scope: 'global' | 'school' | 'class' | string;
  school_id: number | null;
  school_name?: string | null;
  classroom_id: number | null;
  classroom_name?: string | null;
  status: 'approved' | 'pending' | 'rejected' | string;
  tags: string[];
  created_at: string | null;
  comments_count: number;
  likes_count?: number;
  is_liked?: boolean;
  adores_count?: number;
  is_adored?: boolean;
  can_moderate: boolean;
  can_delete?: boolean;
  images?: Array<{ url: string; alt: string | null }>;
  videos?: Array<{ url: string; alt: string | null }>;
  attachments?: Array<{ id: number; name: string | null; type: string | null; size: number | null; url: string }>;
  event: null | {
    title: string | null;
    start_at: string | null;
    end_at: string | null;
    location: string | null;
    capacity: number | null;
    registrations: number;
    is_registered: boolean;
  };
};

type ApiComment = {
  id: number;
  post_id: number;
  parent_comment_id: number | null;
  author: { id: number; name: string | null; role: string | null; avatar_url?: string | null };
  content: string;
  created_at: string | null;
  likes_count: number;
  is_liked: boolean;
  adores_count: number;
  is_adored: boolean;
};

type UiComment = {
  id: string;
  postId: string;
  parentCommentId: string | null;
  authorId: string;
  authorName: string;
  authorRole: Post['authorRole'];
  authorAvatar?: string;
  content: string;
  timestamp: Date;
  likes: number;
  isLiked: boolean;
  adores: number;
  isAdored: boolean;
};

type ApiReactionItem = {
  user: { id: number; name: string | null; role: string | null; avatar_url?: string | null };
  created_at: string | null;
};

function toAbsoluteUrl(url: string): string {
  const u = (url || '').toString().trim();
  if (!u) return '';
  if (/^(data:|blob:)/i.test(u)) return u;
  if (/^https?:\/\//i.test(u)) return u;
  const base = (api.defaults.baseURL || '').toString().replace(/\/+$/, '');
  if (!base) return u;
  return u.startsWith('/') ? `${base}${u}` : `${base}/${u}`;
}

function backendRoleToUi(role: string | null | undefined): Post['authorRole'] {
  const r = (role || '').toString().trim().toLowerCase();
  if (r === 'profesor') return 'teacher';
  if (r === 'elev') return 'student';
  if (r === 'parinte') return 'parent';
  if (r === 'secretariat') return 'secretariat';
  return 'admin';
}

function scopeToVisibility(scope: string): Post['visibility'] {
  const s = (scope || '').toString().trim().toLowerCase();
  if (s === 'global') return 'public';
  if (s === 'class') return 'class';
  return 'school';
}

function visibilityToScope(v: Post['visibility']): 'global' | 'school' | 'class' {
  if (v === 'public') return 'global';
  if (v === 'class') return 'class';
  return 'school';
}

function toUiPost(p: ApiPost): Post {
  const ts = p.created_at ? new Date(p.created_at) : new Date();
  return {
    id: String(p.id),
    authorId: String(p.author?.id ?? ''),
    authorName: p.author?.name || 'Utilizator',
    authorRole: backendRoleToUi(p.author?.role),
    authorAvatar: p.author?.avatar_url ? toAbsoluteUrl(p.author.avatar_url) : undefined,
    content: p.content || '',
    schoolId: p.school_id == null ? null : Number(p.school_id),
    schoolName: p.school_name ?? null,
    classroomId: p.classroom_id == null ? null : Number(p.classroom_id),
    classroomName: p.classroom_name ?? null,
    images: Array.isArray(p.images)
      ? p.images
          .filter((x) => x && x.url)
          .map((x) => ({ url: toAbsoluteUrl(x.url), alt: (x.alt || 'Imagine').toString() }))
      : [],
    videos: Array.isArray(p.videos)
      ? p.videos
          .filter((x) => x && x.url)
          .map((x) => ({ url: toAbsoluteUrl(x.url), alt: (x.alt || 'Video').toString() }))
      : [],
    attachments: Array.isArray(p.attachments)
      ? p.attachments
          .filter((x) => x && x.url)
          .map((x) => ({
            id: String(x.id),
            name: (x.name || 'Fisier').toString(),
            type: (x.type || '').toString(),
            size: Number(x.size || 0),
            url: toAbsoluteUrl(x.url),
          }))
      : [],
    timestamp: ts,
    likes: Number(p.likes_count || 0),
    adores: Number(p.adores_count || 0),
    comments: Number(p.comments_count || 0),
    shares: 0,
    isLiked: Boolean(p.is_liked),
    isAdored: Boolean(p.is_adored),
    isPinned: false,
    category: (p.event ? 'event' : (p.category as any)) || 'discussion',
    visibility: scopeToVisibility(p.scope),
    tags: Array.isArray(p.tags) ? p.tags : [],
    status: (p.status as any) || 'approved',
    canModerate: Boolean(p.can_moderate),
    canDelete: Boolean(p.can_delete),
    event: p.event
      ? {
          title: p.event.title,
          startAt: p.event.start_at ? new Date(p.event.start_at) : null,
          endAt: p.event.end_at ? new Date(p.event.end_at) : null,
          location: p.event.location,
          capacity: p.event.capacity,
          registrations: Number(p.event.registrations || 0),
          isRegistered: Boolean(p.event.is_registered),
        }
      : null,
  };
}

const SchoolSocialPlatform: React.FC = () => {
  const navigate = useNavigate();
  const { me, signOut } = useAuth();
  const COMMENTS_PREVIEW_LIMIT = 3;
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<SubscriptionFeature>('generic');

  const openUpgrade = (feature: SubscriptionFeature) => {
    setUpgradeFeature(feature);
    setUpgradeOpen(true);
  };

  const [isCreatePostOpen, setIsCreatePostOpen] = useState(false);
  const [isAwardsSettingsOpen, setIsAwardsSettingsOpen] = useState(false);
  const [presetCreateCategory, setPresetCreateCategory] = useState<Post['category'] | undefined>(
    undefined
  );
  const [filter, setFilter] = useState<PostFilter>({
    scope: 'mixed',
    category: 'all',
    role: 'all',
    sortBy: 'recent',
    tag: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [loadingError, setLoadingError] = useState<string | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [myClasses, setMyClasses] = useState<ClassroomInSchool[]>([]);
  const [commentsByPost, setCommentsByPost] = useState<Record<string, UiComment[]>>({});
  const [commentsHasMoreByPost, setCommentsHasMoreByPost] = useState<Record<string, boolean>>({});
  const [commentsLoadingByPost, setCommentsLoadingByPost] = useState<Record<string, boolean>>({});
  const [commentsModeByPost, setCommentsModeByPost] = useState<Record<string, 'preview' | 'all'>>({});
  const [reactionsModal, setReactionsModal] = useState<{
    isOpen: boolean;
    title: string;
    isLoading: boolean;
    items: Array<{ id: string; name: string; role: string; avatarUrl?: string }>;
  }>({ isOpen: false, title: '', isLoading: false, items: [] });

  const currentUser: User = useMemo(
    () => ({
      id: String(me?.id || ''),
      name: me?.full_name || 'Utilizator',
      role: backendRoleToUi(me?.role || 'admin'),
      avatar: me?.avatar_url ? toAbsoluteUrl(me.avatar_url) : undefined,
      schoolId: String(me?.school_id || ''),
      schoolName: me?.school_name || 'Școala mea',
    }),
    [me?.avatar_url, me?.full_name, me?.id, me?.role, me?.school_id, me?.school_name]
  );

  const canPost = useMemo(() => {
    const r = (me?.role || '').toString().toLowerCase();
    return ['profesor', 'admin', 'secretariat', 'elev'].includes(r);
  }, [me?.role]);

  const canManageAwards = useMemo(() => {
    const r = (me?.role || '').toString().toLowerCase();
    return r === 'profesor' || r === 'admin';
  }, [me?.role]);

  const classOptions = useMemo(() => {
    return (myClasses || [])
      .filter((c) => c.is_admin || c.is_member)
      .map((c) => ({ value: Number(c.id), label: c.name }));
  }, [myClasses]);

  const allowedVisibilities = useMemo((): Post['visibility'][] => {
    const r = (me?.role || '').toString().toLowerCase();
    if (r === 'admin' || r === 'profesor') {
      return ['public', 'school', ...(classOptions.length ? (['class'] as const) : [])];
    }
    if (r === 'secretariat') return ['public', 'school'];
    if (r === 'elev') return ['public', 'school', 'class'];
    return ['school'];
  }, [classOptions.length, me?.role]);

  const defaultCreateVisibility = useMemo((): Post['visibility'] => {
    const r = (me?.role || '').toString().toLowerCase();
    if (r === 'secretariat') return 'public';
    return 'school';
  }, [me?.role]);

  const openCreatePost = (category?: Post['category']) => {
    if (!canPost) {
      alert('Nu ai permisiunea să postezi.');
      return;
    }
    setPresetCreateCategory(category);
    setIsCreatePostOpen(true);
  };

  const handleFilterChange = (next: PostFilter) => {
    const prevScope = filter.scope || 'mixed';
    const nextScope = next.scope || 'mixed';
    if (prevScope !== nextScope) {
      setFilter({ ...next, schoolId: null, classroomId: null });
      return;
    }
    setFilter(next);
  };

  const loadFeed = async () => {
    setIsLoading(true);
    setLoadingError(null);
    try {
      const resp = await api.get<{ items: ApiPost[] }>('/api/community/feed', {
        params: {
          scope: filter.scope || 'mixed',
          ...(filter.scope === 'school' && filter.schoolId ? { school_id: Number(filter.schoolId) } : {}),
          ...(filter.scope === 'class' && filter.classroomId ? { classroom_id: Number(filter.classroomId) } : {}),
          include_pending: true,
          limit: 100,
        },
      });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      setPosts(items.map(toUiPost));
    } catch (e: any) {
      setLoadingError(e?.response?.data?.detail || 'Nu s-a putut încărca feed-ul.');
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!me) return;
    void loadFeed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.id, filter.scope]);

  useEffect(() => {
    const r = (me?.role || '').toString().toLowerCase();
    if (!me || (r !== 'profesor' && r !== 'admin')) return;
    void (async () => {
      try {
        const cls = await fetchClassesInMySchool();
        setMyClasses(cls || []);
      } catch {
        setMyClasses([]);
      }
    })();
  }, [me]);

  const displayedPosts = useMemo(() => {
    let list = posts.slice();
    if (filter.category !== 'all') list = list.filter((p) => p.category === filter.category);
    if (filter.role !== 'all') list = list.filter((p) => p.authorRole === filter.role);
    if ((filter.tag || '').toString().trim()) {
      const needle = (filter.tag || '').toString().trim().toLowerCase();
      list = list.filter((p) => (p.tags || []).some((t) => (t || '').toString().trim().toLowerCase() === needle));
    }
    if (filter.sortBy === 'popular') {
      list.sort((a, b) => (b.comments || 0) - (a.comments || 0));
    } else {
      list.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    }
    return list;
  }, [filter.category, filter.role, filter.sortBy, filter.tag, posts]);

  const trendingTopics: TrendingTopic[] = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of posts) {
      for (const t of p.tags || []) {
        const k = (t || '').toString().trim();
        if (!k) continue;
        counts.set(k, (counts.get(k) || 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, postCount], idx) => ({ id: `topic-${idx}`, name, postCount, category: 'Community' }));
  }, [posts]);

  const upcomingEvents: SchoolEvent[] = useMemo(() => {
    const ev = posts
      .filter((p) => Boolean(p.event))
      .map((p) => ({
        id: p.id,
        title: p.event?.title || 'Eveniment',
        description: p.content || '',
        date: p.event?.startAt || p.timestamp,
        location: p.event?.location || '—',
        attendees: p.event?.registrations || 0,
        isAttending: Boolean(p.event?.isRegistered),
        category: 'social' as const,
      }));
    return ev.sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 6);
  }, [posts]);

  const communityHighlights: CommunityHighlight[] = useMemo(() => [], []);

  const mapComment = (c: ApiComment): UiComment => {
    const ts = c.created_at ? new Date(c.created_at) : new Date();
    return {
      id: String(c.id),
      postId: String(c.post_id),
      parentCommentId: c.parent_comment_id == null ? null : String(c.parent_comment_id),
      authorId: String(c.author?.id ?? ''),
      authorName: c.author?.name || 'Utilizator',
      authorRole: backendRoleToUi(c.author?.role),
      authorAvatar: c.author?.avatar_url ? toAbsoluteUrl(c.author.avatar_url) : undefined,
      content: c.content || '',
      timestamp: ts,
      likes: Number(c.likes_count || 0),
      isLiked: Boolean(c.is_liked),
      adores: Number(c.adores_count || 0),
      isAdored: Boolean(c.is_adored),
    };
  };

  const getCommentSetForPreview = (all: UiComment[]) => {
    const roots = all.filter((c) => !c.parentCommentId);
    const byParent = new Map<string, UiComment[]>();
    for (const c of all) {
      if (!c.parentCommentId) continue;
      const arr = byParent.get(c.parentCommentId) || [];
      arr.push(c);
      byParent.set(c.parentCommentId, arr);
    }
    for (const arr of byParent.values()) {
      arr.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    }
    roots.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    const selectedRoots = roots.slice(0, COMMENTS_PREVIEW_LIMIT);
    const selected = new Map<string, UiComment>();
    for (const r of selectedRoots) selected.set(r.id, r);
    for (const r of selectedRoots) {
      const replies = byParent.get(r.id) || [];
      for (const rep of replies.slice(0, 2)) {
        selected.set(rep.id, rep);
      }
    }
    return {
      items: Array.from(selected.values()),
      hasMoreRoots: roots.length > COMMENTS_PREVIEW_LIMIT,
    };
  };

  const loadCommentsPreview = async (postId: string) => {
    if (commentsLoadingByPost[postId]) return;
    setCommentsLoadingByPost((prev) => ({ ...prev, [postId]: true }));
    try {
      const resp = await api.get<{ items: ApiComment[] }>(`/api/community/posts/${postId}/comments`, {
        params: { limit: 50, order: 'desc' },
      });
      const raw = Array.isArray(resp.data?.items) ? resp.data.items : [];
      const mapped = raw.map(mapComment);
      const { items, hasMoreRoots } = getCommentSetForPreview(mapped);
      setCommentsByPost((prev) => ({ ...prev, [postId]: items }));
      setCommentsHasMoreByPost((prev) => ({ ...prev, [postId]: hasMoreRoots }));
      setCommentsModeByPost((prev) => ({ ...prev, [postId]: 'preview' }));
    } catch {
      // ignore
    } finally {
      setCommentsLoadingByPost((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const loadCommentsAll = async (postId: string) => {
    if (commentsLoadingByPost[postId]) return;
    setCommentsLoadingByPost((prev) => ({ ...prev, [postId]: true }));
    try {
      const resp = await api.get<{ items: ApiComment[] }>(`/api/community/posts/${postId}/comments`, {
        params: { limit: 200, order: 'asc' },
      });
      const raw = Array.isArray(resp.data?.items) ? resp.data.items : [];
      setCommentsByPost((prev) => ({ ...prev, [postId]: raw.map(mapComment) }));
      setCommentsHasMoreByPost((prev) => ({ ...prev, [postId]: false }));
      setCommentsModeByPost((prev) => ({ ...prev, [postId]: 'all' }));
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut încărca lista de comentarii.');
    } finally {
      setCommentsLoadingByPost((prev) => ({ ...prev, [postId]: false }));
    }
  };

  const refreshCommentsForPost = async (postId: string) => {
    const mode = commentsModeByPost[postId] || 'preview';
    if (mode === 'all') return loadCommentsAll(postId);
    return loadCommentsPreview(postId);
  };

  const openPostReactions = async (postId: string, reaction: 'thumb' | 'adore') => {
    const title = reaction === 'thumb' ? 'Aprecieri (👍)' : 'Adoruri (❤️)';
    setReactionsModal({ isOpen: true, title, isLoading: true, items: [] });
    try {
      const endpoint = reaction === 'thumb' ? `/api/community/posts/${postId}/thumb-likes` : `/api/community/posts/${postId}/adores`;
      const resp = await api.get<{ items: ApiReactionItem[] }>(endpoint, { params: { limit: 200 } });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      setReactionsModal({
        isOpen: true,
        title,
        isLoading: false,
        items: items.map((x) => ({
          id: String(x.user?.id ?? ''),
          name: x.user?.name || 'Utilizator',
          role: (x.user?.role || '').toString(),
          avatarUrl: x.user?.avatar_url ? toAbsoluteUrl(x.user.avatar_url) : undefined,
        })),
      });
    } catch (e: any) {
      setReactionsModal({ isOpen: true, title, isLoading: false, items: [] });
      alert(e?.response?.data?.detail || 'Nu s-a putut încărca lista de reacții.');
    }
  };

  const openCommentReactions = async (postId: string, commentId: string, reaction: 'thumb' | 'adore') => {
    const title = reaction === 'thumb' ? 'Aprecieri (👍)' : 'Adoruri (❤️)';
    setReactionsModal({ isOpen: true, title, isLoading: true, items: [] });
    try {
      const endpoint =
        reaction === 'thumb'
          ? `/api/community/posts/${postId}/comments/${commentId}/thumb-likes`
          : `/api/community/posts/${postId}/comments/${commentId}/adores`;
      const resp = await api.get<{ items: ApiReactionItem[] }>(endpoint, { params: { limit: 200 } });
      const items = Array.isArray(resp.data?.items) ? resp.data.items : [];
      setReactionsModal({
        isOpen: true,
        title,
        isLoading: false,
        items: items.map((x) => ({
          id: String(x.user?.id ?? ''),
          name: x.user?.name || 'Utilizator',
          role: (x.user?.role || '').toString(),
          avatarUrl: x.user?.avatar_url ? toAbsoluteUrl(x.user.avatar_url) : undefined,
        })),
      });
    } catch (e: any) {
      setReactionsModal({ isOpen: true, title, isLoading: false, items: [] });
      alert(e?.response?.data?.detail || 'Nu s-a putut încărca lista de reacții.');
    }
  };

  const handleCreatePost = async (data: CreatePostData) => {
    const r = (me?.role || '').toString().toLowerCase();
    if (!['profesor', 'admin', 'secretariat', 'elev'].includes(r)) {
      alert('Nu ai permisiunea să postezi.');
      return;
    }

    const scope = visibilityToScope(data.visibility);
    const form = new FormData();
    form.append('content', data.content);
    form.append('category', data.category);
    form.append('scope', scope);
    if (data.tags?.length) form.append('tags', data.tags.join(','));
    if (scope === 'class' && data.targetClassroomId) form.append('classroom_id', String(data.targetClassroomId));

    if (data.category === 'event') {
      form.append('is_event', 'true');
      if (data.eventTitle) form.append('event_title', data.eventTitle);
      if (data.eventStartAt) form.append('event_start_at', new Date(data.eventStartAt).toISOString());
      if (data.eventEndAt) form.append('event_end_at', new Date(data.eventEndAt).toISOString());
      if (data.eventLocation) form.append('event_location', data.eventLocation);
      if (data.eventCapacity != null) form.append('event_capacity', String(data.eventCapacity));
    }

    for (const img of data.images || []) {
      form.append('images', img, img.name);
    }
    for (const v of data.videos || []) {
      form.append('videos', v, v.name);
    }

    try {
      const resp = await api.post('/api/community/posts-with-media', form);
      const status = resp?.data?.status;
      if (status === 'pending') {
        alert('Postarea a fost trimisă spre aprobare.');
      }
      await loadFeed();
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        openUpgrade('posts');
        return;
      }
      alert(e?.response?.data?.detail || 'Nu s-a putut publica postarea.');
    }
  };

  const handleComment = async (postId: string, content: string) => {
    try {
      await api.post(`/api/community/posts/${postId}/comments`, { content });
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, comments: (p.comments || 0) + 1 } : p)));
      await refreshCommentsForPost(postId);
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        openUpgrade('comments');
        return;
      }
      alert(e?.response?.data?.detail || 'Nu s-a putut adăuga comentariul.');
    }
  };

  const handleReplyToComment = async (postId: string, parentCommentId: string, content: string) => {
    try {
      await api.post(`/api/community/posts/${postId}/comments`, { content, parent_comment_id: Number(parentCommentId) });
      setPosts((prev) => prev.map((p) => (p.id === postId ? { ...p, comments: (p.comments || 0) + 1 } : p)));
      await refreshCommentsForPost(postId);
    } catch (e: any) {
      if (isSubscriptionError(e)) {
        openUpgrade('comments');
        return;
      }
      alert(e?.response?.data?.detail || 'Nu s-a putut adăuga răspunsul.');
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    const ok = window.confirm('Ștergi acest comentariu?');
    if (!ok) return;
    try {
      const resp = await api.delete(`/api/community/posts/${postId}/comments/${commentId}`);
      const deletedCount = Math.max(1, Number(resp?.data?.deleted_count || 1));
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, comments: Math.max(0, (p.comments || 0) - deletedCount) } : p))
      );
      await refreshCommentsForPost(postId);
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut șterge comentariul.');
    }
  };

  const handleDeletePost = async (postId: string) => {
    const p = posts.find((x) => x.id === postId);
    if (!p?.canDelete) {
      alert('Nu ai permisiunea să ștergi această postare.');
      return;
    }
    const reason = window.prompt('Motivul ștergerii (obligatoriu):', '');
    if (reason == null) return;
    const reasonNorm = String(reason || '').trim();
    if (!reasonNorm) {
      alert('Motivul ștergerii este obligatoriu.');
      return;
    }
    const ok = window.confirm('Ștergi această postare? Acțiunea este ireversibilă.');
    if (!ok) return;
    try {
      await api.delete(`/api/community/posts/${postId}`, { data: { reason: reasonNorm } });
      setPosts((prev) => prev.filter((p) => p.id !== postId));
      setCommentsByPost((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      setCommentsHasMoreByPost((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      setCommentsLoadingByPost((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
      setCommentsModeByPost((prev) => {
        const next = { ...prev };
        delete next[postId];
        return next;
      });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut șterge postarea.');
    }
  };

  const handleThumbLikeComment = async (postId: string, commentId: string) => {
    const list = commentsByPost[postId] || [];
    const c = list.find((x) => x.id === commentId);
    const nextLike = !c?.isLiked;
    try {
      if (nextLike) {
        await api.post(`/api/community/posts/${postId}/comments/${commentId}/thumb-like`, {});
      } else {
        await api.delete(`/api/community/posts/${postId}/comments/${commentId}/thumb-like`);
      }
      setCommentsByPost((prev) => {
        const cur = prev[postId] || [];
        return {
          ...prev,
          [postId]: cur.map((x) =>
            x.id === commentId ? { ...x, isLiked: nextLike, likes: Math.max(0, (x.likes || 0) + (nextLike ? 1 : -1)) } : x
          ),
        };
      });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut actualiza like-ul comentariului.');
    }
  };

  const handleAdoreComment = async (postId: string, commentId: string) => {
    const list = commentsByPost[postId] || [];
    const c = list.find((x) => x.id === commentId);
    const nextAdore = !c?.isAdored;
    try {
      if (nextAdore) {
        await api.post(`/api/community/posts/${postId}/comments/${commentId}/like`, {});
      } else {
        await api.delete(`/api/community/posts/${postId}/comments/${commentId}/like`);
      }
      setCommentsByPost((prev) => {
        const cur = prev[postId] || [];
        return {
          ...prev,
          [postId]: cur.map((x) =>
            x.id === commentId ? { ...x, isAdored: nextAdore, adores: Math.max(0, (x.adores || 0) + (nextAdore ? 1 : -1)) } : x
          ),
        };
      });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut actualiza reacția comentariului.');
    }
  };

  const handleThumbLikePost = async (postId: string) => {
    const current = posts.find((p) => p.id === postId);
    const nextLike = !current?.isLiked;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId ? { ...p, isLiked: nextLike, likes: Math.max(0, (p.likes || 0) + (nextLike ? 1 : -1)) } : p
      )
    );
    try {
      if (nextLike) {
        await api.post(`/api/community/posts/${postId}/thumb-like`, {});
      } else {
        await api.delete(`/api/community/posts/${postId}/thumb-like`);
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut actualiza like-ul.');
      await loadFeed();
    }
  };

  const handleAdorePost = async (postId: string) => {
    const current = posts.find((p) => p.id === postId);
    const nextAdore = !current?.isAdored;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === postId
          ? { ...p, isAdored: nextAdore, adores: Math.max(0, (p.adores || 0) + (nextAdore ? 1 : -1)) }
          : p
      )
    );
    try {
      if (nextAdore) {
        await api.post(`/api/community/posts/${postId}/like`, {});
      } else {
        await api.delete(`/api/community/posts/${postId}/like`);
      }
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut actualiza reacția.');
      await loadFeed();
    }
  };

  const handleShare = async (postId: string) => {
    const base = window.location.href.split('#')[0];
    const url = `${base}#post-${postId}`;
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copiat.');
    } catch {
      window.prompt('Copiază link-ul:', url);
    }
  };

  const handleModerate = async (
    postId: string,
    action: 'approve' | 'reject',
    visibility?: Post['visibility']
  ) => {
    try {
      const scope = visibility ? visibilityToScope(visibility) : undefined;
      await api.patch(`/api/community/posts/${postId}/moderate`, {
        action,
        ...(action === 'approve' && scope ? { scope } : {}),
      });
      await loadFeed();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut modifica statusul postării.');
    }
  };

  const handleDestinationClick = (post: Post) => {
    const scope = visibilityToScope(post.visibility);
    if (scope === 'global') {
      setFilter((prev) => ({ ...prev, scope: 'global', schoolId: null, classroomId: null }));
      return;
    }
    if (scope === 'school') {
      setFilter((prev) => ({ ...prev, scope: 'school', schoolId: post.schoolId ?? null, classroomId: null }));
      return;
    }
    setFilter((prev) => ({ ...prev, scope: 'class', classroomId: post.classroomId ?? null, schoolId: null }));
  };

  const handleToggleEventRegistration = async (postId: string, nextRegister: boolean) => {
    try {
      if (nextRegister) {
        await api.post(`/api/community/events/${postId}/register`, {});
      } else {
        await api.delete(`/api/community/events/${postId}/register`);
      }
      await loadFeed();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Nu s-a putut actualiza înscrierea.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={{
          role: currentUser.role,
          name: currentUser.name,
          avatar: currentUser.avatar,
        }}
        onSignOut={() => {
          signOut();
          navigate('/authentication-portal');
        }}
      />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 pt-6 pb-24 sm:pb-6">
          <CommunicationNotifier messages={[]} />

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-foreground">Platformă socială școlară</h1>
            <p className="text-sm text-muted-foreground">Conectează-te, distribuie și implică-te în comunitate.</p>
          </div>

          {canManageAwards ? (
            <div className="flex items-center justify-end mb-3">
              <button
                type="button"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border bg-card hover:bg-muted text-sm"
                onClick={() => setIsAwardsSettingsOpen(true)}
              >
                <Icon name="Settings" size={16} className="text-muted-foreground" />
                <span>Setări premii</span>
              </button>
            </div>
          ) : null}

          <FilterBar filter={filter} onFilterChange={handleFilterChange} onCreatePost={() => openCreatePost()} />
          <CreatePostFab onCreate={openCreatePost} />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {isLoading && posts.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                </div>
              ) : loadingError ? (
                <div className="bg-error/10 border border-error/20 text-error rounded-md p-3 text-sm">{loadingError}</div>
              ) : displayedPosts.length === 0 ? (
                <div className="text-sm text-muted-foreground">Nu există postări pentru filtrul ales.</div>
              ) : (
                displayedPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    currentUserId={currentUser.id}
                    canDeletePost={Boolean(post.canDelete)}
                    onDeletePost={handleDeletePost}
                    onThumbLike={handleThumbLikePost}
                    onAdore={handleAdorePost}
                    onViewPostReactions={openPostReactions}
                    onComment={handleComment}
                    onShare={handleShare}
                    onViewComments={loadCommentsAll}
                    onDestinationClick={handleDestinationClick}
                    onApprove={(id, visibility) => handleModerate(id, 'approve', visibility)}
                    onReject={(id) => handleModerate(id, 'reject')}
                    onToggleEventRegistration={handleToggleEventRegistration}
                    comments={commentsByPost[post.id] || []}
                    commentsLoading={Boolean(commentsLoadingByPost[post.id])}
                    commentsHasMore={Boolean(commentsHasMoreByPost[post.id])}
                    onLoadCommentsPreview={loadCommentsPreview}
                    onReplyComment={handleReplyToComment}
                    onThumbLikeComment={handleThumbLikeComment}
                    onAdoreComment={handleAdoreComment}
                    onViewCommentReactions={openCommentReactions}
                    onDeleteComment={handleDeleteComment}
                  />
                ))
              )}
              {isLoading && posts.length > 0 ? (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Icon name="Loader2" size={14} className="animate-spin" />
                  <span>Se actualizează…</span>
                </div>
              ) : null}
            </div>

            <div className="lg:col-span-1">
              <TrendingSidebar
                trendingTopics={trendingTopics}
                upcomingEvents={upcomingEvents}
                communityHighlights={communityHighlights}
                selectedTopic={filter.tag || null}
                onTopicClick={(tag) =>
                  setFilter((prev) => {
                    const next = (tag || '').toString().trim();
                    const prevTag = (prev.tag || '').toString().trim();
                    const same = prevTag && next && prevTag.toLowerCase() === next.toLowerCase();
                    return { ...prev, category: 'all', tag: same || !next ? null : next };
                  })
                }
                onEventAttend={(eventId) => {
                  const post = posts.find((p) => p.id === eventId);
                  void handleToggleEventRegistration(eventId, !post?.event?.isRegistered);
                }}
              />
            </div>
          </div>

          {(filter.tag || '').toString().trim() ? (
            <div className="mt-3 flex items-center justify-between gap-2 text-sm">
              <div className="text-muted-foreground">
                Filtru tag: <span className="text-foreground font-medium">#{String(filter.tag).trim()}</span>
              </div>
              <button
                type="button"
                className="text-xs px-2 py-1 rounded-md border border-border hover:bg-muted"
                onClick={() => setFilter((prev) => ({ ...prev, tag: null }))}
              >
                Elimină
              </button>
            </div>
          ) : null}

          <CreatePostModal
            isOpen={isCreatePostOpen}
            onClose={() => {
              setIsCreatePostOpen(false);
              setPresetCreateCategory(undefined);
            }}
            onSubmit={handleCreatePost}
            userAvatar={currentUser.avatar}
            userName={currentUser.name}
            allowedVisibilities={allowedVisibilities}
            initialVisibility={defaultCreateVisibility}
            classOptions={classOptions}
            initialCategory={presetCreateCategory}
          />

          <AwardsSettingsModal
            open={isAwardsSettingsOpen}
            onClose={() => setIsAwardsSettingsOpen(false)}
            onChanged={() => void loadFeed()}
          />

          <ReactionsModal
            isOpen={reactionsModal.isOpen}
            title={reactionsModal.title}
            items={reactionsModal.items}
            isLoading={reactionsModal.isLoading}
            onClose={() => setReactionsModal((prev) => ({ ...prev, isOpen: false }))}
          />
        </div>
      </main>

      <SubscriptionRequiredModal
        open={upgradeOpen}
        feature={upgradeFeature}
        onClose={() => setUpgradeOpen(false)}
      />
    </div>
  );
};

export default SchoolSocialPlatform;
